import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { KyrgyzstanEmpty } from 'entities/kyrgyzstan/ui'

import type { EditObjectDraftRevision } from 'shared/api/kyrgyzstan/edit-object'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { HeaderGoBack } from 'shared/ui/header-go-back'

import {
  type EditObjectListItem,
  buildEditObjectMenuItemsFromRevision,
} from '../../model/edit-object-menu-items'
import s from '../../styles.module.scss'
import { EditObjectEditorSkeleton } from '../edit-object-editor-skeleton'
import { EditObjectMenu } from '../edit-object-menu'
import { RejectionReasonAlert } from '../rejection-reason-alert'

export interface EditObjectEditorProps {
  actionLabel: string
  bookingConfirmed: boolean
  draft: EditObjectDraftRevision | null
  error: string | null
  isDirty: boolean
  isInitialLoading: boolean
  isSubmitting: boolean
  rejectionReason?: string | null
  onBack: () => void
  onDelete: () => void
  onItemClick: (item: EditObjectListItem) => void
  onSubmit: () => void
}

export const EditObjectEditor = ({
  actionLabel,
  bookingConfirmed,
  draft,
  error,
  isDirty,
  isInitialLoading,
  isSubmitting,
  rejectionReason,
  onBack,
  onDelete,
  onItemClick,
  onSubmit,
}: EditObjectEditorProps) => {
  const { t } = useTranslation()
  const draftId = draft?.id ?? null
  const menuItems = buildEditObjectMenuItemsFromRevision(draft, t)
  const isActionDisabled =
    isInitialLoading || isSubmitting || Boolean(error) || !draftId || !isDirty

  const content = isInitialLoading ? (
    <EditObjectEditorSkeleton />
  ) : error ? (
    <KyrgyzstanEmpty
      title={t('kyrgyzstan_block.error.title')}
      subtitle={error}
    />
  ) : (
    <>
      {rejectionReason ? (
        <RejectionReasonAlert reason={rejectionReason} />
      ) : null}

      <EditObjectMenu
        items={menuItems}
        bookingConfirmed={bookingConfirmed}
        disabled={isInitialLoading || isSubmitting || !draftId}
        onItemClick={onItemClick}
        onDeleteClick={onDelete}
      />
    </>
  )

  return (
    <main>
      <HeaderGoBack
        variant="grey"
        buttonWhite
        title={t('kyrgyzstan_block.edit_object.title')}
        onGoBack={onBack}
      />

      <div className={s.content}>{content}</div>

      <ButtonContinue
        type="button"
        className={clsx(s.saveButton, 'semi-bold-17')}
        disabled={isActionDisabled}
        loading={isSubmitting}
        onClick={onSubmit}
      >
        {actionLabel}
      </ButtonContinue>
    </main>
  )
}
